import * as d3 from 'd3';
import { defineTexturedGlassPatterns } from './glassPatternsDefs';

export function dN_Circle_Oculus(svg, d, x, y, circles, labelText = null, displayLabel = true, labelOptions = {}) {
  let prev = {};

  const {
    fontSize = 14,
    fontColor = 'black',
    fontFamily = 'Arial',
    fontOpacity = 0.4,
  } = labelOptions;

  circles.forEach((circle, index) => {
    const {
      w,
      h = prev.h ?? 100,
      radiusX = prev.radiusX ?? Math.min(w, h) / 2,
      radiusY = prev.radiusY ?? Math.min(w, h) / 2,
      circleWidth = prev.circleWidth ?? 10,
      color = prev.color ?? 'white',
      glassType = prev.glassType || null,
      glassDesign = prev.glassDesign || null,
      frameWidth = prev.frameWidth ?? 0,
      frameColor = prev.frameColor ?? 'white',
      lineFrameColor = prev.lineFrameColor ?? frameColor,
      lineFrameThickness = prev.lineFrameThickness ?? 1,
      outerStrokeColor = prev.outerStrokeColor ?? 'black',
      outerStrokeWidth = prev.outerStrokeWidth ?? 1,
    } = circle;

    const centerX = x + w / 2;
    const centerY = y + h / 2;

    let baseFill = color;
    let tintOverlay = null;

    // TINTED GLASS
    if (glassType === 'Tinted') {
      baseFill = 'white';
      switch (glassDesign?.toLowerCase()) {
        case 'grey':
          tintOverlay = 'rgba(60, 60, 60, 0.2)';
          break;
        case 'bronze':
          tintOverlay = 'rgba(90, 50, 0, 0.2)';
          break;
        default:
          console.warn('Unknown tinted glass design:', glassDesign);
      }
    }

    // TEXTURED GLASS
    if (glassType === 'Textured' && glassDesign) {
      const patternId = `${glassDesign.toLowerCase().replace(/\s/g, '')}Pattern`;
      tintOverlay = `url(#${patternId})`;
      defineTexturedGlassPatterns(svg, glassDesign);
    }

    // === RECTANGULAR FRAME - The area between rectangle and outer ellipse ===
    if (frameWidth > 0) {
      svg.append('rect')
        .attr('x', x)
        .attr('y', y)
        .attr('width', w)
        .attr('height', h)
        .attr('fill', frameColor)
        .attr('stroke', lineFrameColor)
        .attr('stroke-width', 0.1);
    }

    if (frameWidth > 0) {
        const x1 = x - frameWidth/2;
        const y1 = y - frameWidth/2;
        const x2 = x + w + frameWidth/2;
        const y2 = y + h + frameWidth/2;

        // Top line
        svg.append('line')
          .attr('x1', x1)
          .attr('y1', y1)
          .attr('x2', x2)
          .attr('y2', y1)
          .attr('stroke', lineFrameColor)
          .attr('stroke-width', 0.5);

        // Right line
        svg.append('line')
          .attr('x1', x2 + 1)
          .attr('y1', y1)
          .attr('x2', x2 + 1)
          .attr('y2', y2)
          .attr('stroke', lineFrameColor)
          .attr('stroke-width', 0.5);

        // Bottom line
        svg.append('line')
          .attr('x1', x2)
          .attr('y1', y2 + 1)
          .attr('x2', x1)
          .attr('y2', y2 + 1)
          .attr('stroke', lineFrameColor)
          .attr('stroke-width', 0.5);

        // Left line
        svg.append('line')
          .attr('x1', x1)
          .attr('y1', y2)
          .attr('x2', x1)
          .attr('y2', y1)
          .attr('stroke', lineFrameColor)
          .attr('stroke-width', 0.5);
    }
    // === OUTER FRAME (Ellipse/Circle) - Like a wall frame ===
    if (circleWidth > 0) {
      svg.append('ellipse')
        .attr('cx', centerX)
        .attr('cy', centerY)
        .attr('rx', radiusX + circleWidth)
        .attr('ry', radiusY + circleWidth)
        .attr('fill', frameColor)
        .attr('stroke', lineFrameColor)
        .attr('stroke-width', lineFrameThickness);
    }

    // === BASE FILL (Glass Ellipse/Circle) - The window ===
    svg.append('ellipse')
      .attr('cx', centerX)
      .attr('cy', centerY)
      .attr('rx', radiusX)
      .attr('ry', radiusY)
      .attr('fill', baseFill)
      .attr('stroke', outerStrokeColor)
      .attr('stroke-width', outerStrokeWidth);

    // === TINT OVERLAY ===
    if (tintOverlay) {
      svg.append('ellipse')
        .attr('cx', centerX)
        .attr('cy', centerY)
        .attr('rx', radiusX)
        .attr('ry', radiusY)
        .attr('fill', tintOverlay)
        .attr('stroke', 'none');
    }

    // === LABEL ===
    const textToUse = labelText == null ? index + 1 : labelText;

    if (displayLabel) {
      svg.append('text')
        .attr('x', centerX)
        .attr('y', centerY + fontSize / 3)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', fontColor)
        .attr('fill-opacity', fontOpacity)
        .attr('font-family', fontFamily)
        .text(textToUse);
    }

    prev = {
      w, h, color, radiusX, radiusY, circleWidth,
      frameWidth, frameColor,
      lineFrameColor, lineFrameThickness,
      outerStrokeColor, outerStrokeWidth,
      glassType, glassDesign,
      displayLabel
    };
  });
}
